import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Shield,
  Map,
  Clock,
  FileText,
  Lock,
  Users,
  Check,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SEO, PAGE_SEO } from "@/components/SEO";

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const features = [
  {
    icon: Map,
    title: "Structured maps",
    description:
      "Organise what you own and manage across categories: crypto, investments, property, vehicles, pets, online accounts, and physical records.",
  },
  {
    icon: Shield,
    title: "Privacy by design",
    description:
      "Lifely never stores passwords, seed phrases, or private keys. You record where access is managed \u2014 not the secrets themselves.",
  },
  {
    icon: Clock,
    title: "Continuity check-ins",
    description:
      "Set gentle reminders to review and confirm your maps are current. Choose what happens if you stop responding.",
  },
  {
    icon: Users,
    title: "Trusted contacts",
    description:
      "Nominate someone you trust to view selected information if you become unreachable. Sharing is always on your terms.",
  },
  {
    icon: FileText,
    title: "Printable summaries",
    description:
      "Generate clean, printable overviews of your maps for offline reference or to share with a solicitor or family member.",
  },
  {
    icon: Lock,
    title: "Not a vault",
    description:
      "Lifely is intentionally not a vault, wallet, or legal service. It\u2019s a clarity layer that complements the tools you already use.",
  },
];

const useCases = [
  {
    title: "Self-custodied crypto",
    description:
      "Map wallets, chains, staking positions, and protocol interactions. Note which are critical and where access is managed.",
  },
  {
    title: "Traditional investments",
    description:
      "Document brokerage accounts, managed funds, KiwiSaver, pensions, and other holdings with context about each.",
  },
  {
    title: "Property & vehicles",
    description:
      "Record ownership details, locations, insurance, and any relevant notes about property and vehicle assets.",
  },
  {
    title: "Online accounts",
    description:
      "List important subscriptions, cloud storage, social media, and digital services \u2014 with notes on what matters and what doesn\u2019t.",
  },
  {
    title: "Physical records",
    description:
      "Note where important physical documents are stored: passports, birth certificates, insurance policies, titles.",
  },
  {
    title: "Pets & responsibilities",
    description:
      "Document care instructions, vet details, and preferences for ongoing responsibilities that matter to you.",
  },
];

export default function About() {
  return (
    <div className="pt-24">
      <SEO {...PAGE_SEO.about} />
      {/* Header */}
      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <Breadcrumb className="mb-8">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>About</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4"
            >
              The platform
            </motion.p>
            <motion.h1
              variants={fadeInUp}
              className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground leading-tight max-w-3xl"
            >
              Structured clarity for what matters
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="mt-5 text-lg text-muted-foreground max-w-2xl leading-relaxed"
            >
              Lifely helps you create structured maps of your important
              information, record the context that formal documents miss, and
              ensure continuity for the people who matter most.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="pb-12">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="rounded-2xl overflow-hidden shadow-xl"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_cc441a7d-dbdb-42e3-be6e-af4a1804c84b/artifacts/zsqgfwc3_photo-1602080858428-57174f9431cf.avif"
              alt="MacBook Pro beside clear drinking glass on wooden table"
              className="w-full h-[300px] md:h-[400px] object-cover object-[center_80%]"
            />
          </motion.div>
        </div>
      </section>

      {/* What Lifely does */}
      <section className="py-20 bg-secondary/40">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="mb-14"
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4"
            >
              Features
            </motion.p>
            <motion.h2
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl text-foreground"
            >
              What Lifely does
            </motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                >
                  <Card className="h-full border-border/50 bg-card hover:shadow-md transition-shadow duration-300">
                    <CardContent className="p-7">
                      <div className="w-11 h-11 rounded-xl bg-primary/8 flex items-center justify-center mb-5">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* The gap Lifely fills */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4">
                The problem
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-foreground leading-tight">
                The gap between legal documents and real life
              </h2>
              <p className="mt-5 text-muted-foreground leading-[1.8]">
                A will says who gets what. But it rarely explains what exists,
                why it matters, how to access it, or what to do first. Between
                formal legal updates, life changes constantly — new accounts,
                new assets, new responsibilities.
              </p>
              <p className="mt-4 text-muted-foreground leading-[1.8]">
                For self-custodied digital assets, this gap is critical. There's
                no bank to call, no administrator to contact. If the knowledge
                isn't documented somewhere accessible, those assets may be lost
                permanently.
              </p>
              <p className="mt-4 text-muted-foreground leading-[1.8]">
                Lifely fills that gap. Not as a legal tool or a vault, but as a
                living, breathing orientation map that evolves with your life.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="space-y-6"
            >
              <div className="bg-card rounded-2xl border border-border/50 p-8">
                <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 mb-6 font-medium">
                  What Lifely replaces
                </h3>
                <ul className="space-y-4">
                  {[
                    "Scattered notes and mental checklists",
                    "Assumptions that someone will figure it out",
                    "Expensive legal hours spent on basic orientation",
                    "Lost crypto because nobody knew it existed",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-card rounded-2xl border border-border/50 p-8">
                <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 mb-6 font-medium">
                  What Lifely doesn't do
                </h3>
                <ul className="space-y-4">
                  {[
                    "Store passwords, seeds, or private keys",
                    "Execute transactions or transfers",
                    "Replace your will or legal documents",
                    "Custody crypto or financial assets",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <X className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-20 bg-secondary/40">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="mb-14"
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4"
            >
              Use cases
            </motion.p>
            <motion.h2
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl text-foreground"
            >
              What you can map
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="mt-4 text-muted-foreground text-lg max-w-xl"
            >
              Lifely supports structured mapping across all the categories that
              matter in your life.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className="bg-card rounded-xl border border-border/50 p-6 hover:shadow-md transition-shadow duration-300"
              >
                <h3 className="font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Not a Replacement for a Will */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4">
                Important distinction
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-foreground leading-tight">
                Lifely is not a replacement for a will
              </h2>
              <p className="mt-5 text-muted-foreground leading-[1.8]">
                A will is essential. Lifely does not replace one.
              </p>
              <p className="mt-4 text-foreground leading-[1.8] font-medium">
                Instead, Lifely exists to support it.
              </p>
              <p className="mt-4 text-muted-foreground leading-[1.8]">
                A will captures formal instructions — but life changes faster than most people update legal documents.
              </p>
              <div className="mt-6 space-y-2 text-muted-foreground">
                <p>Assets are bought and sold.</p>
                <p>Crypto portfolios evolve.</p>
                <p>Wallets change.</p>
                <p>New accounts are opened.</p>
                <p>Old experiments are abandoned.</p>
                <p>Side projects begin and end.</p>
              </div>
              <p className="mt-6 text-muted-foreground leading-[1.8]">
                Especially in the world of digital assets — cryptocurrencies, staking, tokenised investments, decentralised finance — there may be no third party capable of reconstructing what existed if knowledge is lost.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <div className="bg-secondary/40 rounded-2xl p-8 border border-border/30">
                <h3 className="text-lg font-semibold text-foreground mb-6">
                  Lifely is designed to capture what a will often cannot:
                </h3>
                <ul className="space-y-4">
                  {[
                    "Orientation",
                    "Context",
                    "Intent",
                    "Where access is intentionally managed",
                    "What is critical vs what can be ignored",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-border/50 space-y-3">
                  <p className="text-muted-foreground">It does not store passwords.</p>
                  <p className="text-muted-foreground">It does not store seed phrases.</p>
                  <p className="text-muted-foreground">It does not custody assets.</p>
                  <p className="text-foreground font-medium mt-4">It simply ensures that nothing important is invisible.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* A Living Companion */}
      <section className="py-20 bg-secondary/40">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="mb-14"
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4"
            >
              How it works
            </motion.p>
            <motion.h2
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl text-foreground"
            >
              A living companion to formal documents
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="mt-4 text-muted-foreground text-lg max-w-2xl"
            >
              Think of Lifely as a living layer that sits alongside your formal documents.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card rounded-2xl border border-border/50 p-8"
            >
              <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 mb-6 font-medium">
                Your will can say
              </h3>
              <p className="text-foreground text-lg italic">
                "All digital assets to my partner."
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-card rounded-2xl border border-border/50 p-8"
            >
              <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 mb-6 font-medium">
                Lifely can say
              </h3>
              <p className="text-foreground leading-relaxed">
                "These assets exist. These are critical. Access is managed through a hardware wallet and a password manager. These experimental wallets are not material. This validator position requires specialist help."
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-12 space-y-6"
          >
            <p className="text-muted-foreground leading-[1.8] max-w-3xl">
              It allows you to update context without rewriting legal structures every time something changes.
            </p>
            <p className="text-muted-foreground leading-[1.8] max-w-3xl">
              It is also a centralised place for your own clarity.
            </p>
            <p className="text-muted-foreground leading-[1.8] max-w-3xl">
              For those who experiment with crypto — multiple wallets, chains, staking protocols, validators — complexity compounds quickly. Even for yourself, it becomes easy to forget where assets are staked, which chains you interacted with, or which wallets matter.
            </p>
            <p className="text-foreground leading-[1.8] max-w-3xl font-medium">
              Lifely helps you map your own digital footprint before anyone else ever needs to.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1666739651802-1d8456958a6c?auto=format&fit=crop&w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#2D2A26]/65" />
        </div>
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white leading-tight">
              Start mapping today
            </h2>
            <p className="mt-5 text-white/60 text-lg">
              Begin with free Insights to learn the framework, or jump straight
              into mapping with Lifely Standard.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/pricing">
                <Button className="h-12 px-8 bg-white text-[#2D2A26] hover:bg-white/90 rounded-full font-medium">
                  See pricing <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/our-story">
                <Button
                  variant="outline"
                  className="h-12 px-8 rounded-full border-white/30 text-white hover:bg-white/10 font-medium"
                >
                  Read our story
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
